import React, { useState, useMemo } from 'react';
import { Button, Card } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

interface HorizontalWeekViewProps {
  selectedDate?: Dayjs;
  onSelectDate?: (date: Dayjs) => void;
  onDateClick?: (date: Dayjs) => void;
}

const HorizontalWeekView: React.FC<HorizontalWeekViewProps> = ({
  selectedDate = dayjs(),
  onSelectDate,
  onDateClick,
}) => {
  const [currentWeek, setCurrentWeek] = useState(selectedDate);

  // Generate 7 days centered around the current day
  const weekDays = useMemo(() => {
    const days = [];
    
    // Start 3 days before the current week's center day
    for (let i = -3; i <= 3; i++) {
      days.push(currentWeek.add(i, 'day'));
    }
    
    return days;
  }, [currentWeek]);

  // Navigation handlers
  const goToPreviousWeek = () => {
    const newWeek = currentWeek.subtract(7, 'day');
    setCurrentWeek(newWeek);
    if (onSelectDate) {
      onSelectDate(newWeek);
    }
  };

  const goToNextWeek = () => {
    const newWeek = currentWeek.add(7, 'day');
    setCurrentWeek(newWeek);
    if (onSelectDate) {
      onSelectDate(newWeek);
    }
  };

  const goToToday = () => {
    const today = dayjs();
    setCurrentWeek(today);
    if (onSelectDate) {
      onSelectDate(today);
    }
  };

  // Handle date selection
  const handleDateClick = (date: Dayjs) => {
    if (onSelectDate) {
      onSelectDate(date);
    }
    if (onDateClick) {
      onDateClick(date);
    }
  };

  // Render a single day card
  const renderDayCard = (date: Dayjs, index: number) => {
    const isToday = date.isSame(dayjs(), 'day');
    const isSelected = date.isSame(selectedDate, 'day');
    const isCurrentMonth = date.isSame(selectedDate, 'month');
    const isCenterCard = index === 3; // 4th card (0-indexed)
    
    return (
      <Card
        key={date.format('YYYY-MM-DD')}
        size="small"
        style={{
          width: 'calc(100% / 7)', // Exactly 1/7th of container width
          flex: '0 0 calc(100% / 7)',
          cursor: 'pointer',
          border: isToday ? '2px solid #1890ff' : '1px solid #f0f0f0',
          backgroundColor: isSelected ? '#e6f7ff' : (isCenterCard ? '#f8f9fa' : 'white'),
          opacity: isCurrentMonth ? 1 : 0.7,
          transition: 'all 0.2s ease',
          boxShadow: isCenterCard ? '0 2px 8px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center',
          padding: '8px 4px',
          margin: '0 2px',
          position: 'relative'
        }}
        bodyStyle={{ padding: '8px 4px' }}
        onClick={() => handleDateClick(date)}
        hoverable
      >
        {/* Month/Year - only show in center card */}
        {isCenterCard && (
          <div style={{
            fontSize: '10px',
            color: '#1890ff',
            fontWeight: 'bold',
            marginBottom: '4px',
            lineHeight: 1
          }}>
            {date.format('MMMM YYYY')}
          </div>
        )}

        {/* Day number */}
        <div style={{ 
          fontSize: isCenterCard ? '18px' : '16px', 
          fontWeight: isToday ? 'bold' : (isCenterCard ? 'bold' : 'normal'),
          color: isToday ? '#1890ff' : (isCenterCard ? '#1890ff' : '#333'),
          marginBottom: '2px',
          lineHeight: 1
        }}>
          {date.format('D')}
        </div>
        
        {/* Day name */}
        <div style={{ 
          fontSize: isCenterCard ? '12px' : '10px', 
          color: isCenterCard ? '#1890ff' : '#666',
          fontWeight: isToday ? 'bold' : (isCenterCard ? 'bold' : 'normal'),
          lineHeight: 1
        }}>
          {date.format('ddd')}
        </div>

        {/* Today indicator */}
        {isToday && (
          <div style={{
            position: 'absolute',
            top: '2px',
            right: '2px',
            width: '6px',
            height: '6px',
            backgroundColor: '#1890ff',
            borderRadius: '50%'
          }} />
        )}

        {/* Center card indicator */}
        {isCenterCard && (
          <div style={{
            position: 'absolute',
            bottom: '2px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '20px',
            height: '2px',
            backgroundColor: '#1890ff',
            borderRadius: '1px'
          }} />
        )}
      </Card>
    );
  };

  return (
    <div>
      {/* Cards row - exactly 7 cards */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'stretch',
        marginBottom: 12,
        width: '100%'
      }}>
        {weekDays.map((date, index) => renderDayCard(date, index))}
      </div>

      {/* Navigation row - centered below cards */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        gap: '16px'
      }}>
        <Button 
          type="text" 
          icon={<LeftOutlined />} 
          onClick={goToPreviousWeek}
          size="small"
          style={{ 
            minWidth: '40px',
            height: '32px'
          }}
        />
        
        <Button 
          size="small"
          onClick={goToToday}
          type="primary"
          style={{ 
            minWidth: '80px',
            height: '32px'
          }}
        >
          Today
        </Button>
        
        <Button 
          type="text" 
          icon={<RightOutlined />} 
          onClick={goToNextWeek}
          size="small"
          style={{ 
            minWidth: '40px',
            height: '32px'
          }}
        />
      </div>
    </div>
  );
};

export default HorizontalWeekView; 